import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { WorkloadEventType, WorkloadOptimizationEvent } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WOOP_EVENTS_QUERY_KEY = 'woop-events';

export type UseWorkloadEventsQueryArgs = {
  clusterId: string;
  workloadId?: string;
  workloadName?: string;
  namespace?: string;
  kind?: string;
  pageLimit?: string;
  fromDate?: string;
  toDate?: string;
  queryKey: string;
  types?: WorkloadEventType[];
  enabled?: boolean;
};

type UseWorkloadEventsQuery = {
  isLoading: boolean;
  events?: WorkloadOptimizationEvent[];
  error?: unknown;
  refetch: () => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetching: boolean;
};

const getWorkloadAutoscalerEventsQueryKey = (
  params: {
    clusterId?: string;
    workloadId?: string;
    workloadName?: string;
    namespace?: string;
    kind?: string;
    pageLimit?: string;
    queryKey?: string;
    types?: WorkloadEventType[];
  } = {}
) =>
  [
    WOOP_EVENTS_QUERY_KEY,
    params.queryKey,
    params.clusterId,
    params.workloadId,
    params.workloadName,
    params.namespace,
    params.kind,
    params.pageLimit,
    ...(params.types ?? []),
  ] as const;

const getWorkloadAutoscalerEventsQueryOptions = ({
  clusterId,
  workloadId,
  workloadName,
  namespace,
  kind,
  fromDate,
  toDate,
  queryKey,
  pageLimit,
  types = [],
  enabled = true,
}: UseWorkloadEventsQueryArgs) =>
  infiniteQueryOptions({
    queryKey: getWorkloadAutoscalerEventsQueryKey({
      clusterId,
      workloadId,
      workloadName,
      namespace,
      kind,
      pageLimit,
      queryKey,
      types,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.workloadOptimization.getEvents({
        clusterId,
        workloadId,
        pageLimit,
        pageCursor: pageParam,
        fromDate,
        toDate,
        workloadName,
        workloadKind: kind,
        workloadNamespace: namespace,
        type: types,
      });
      return data || {};
    },
    enabled,
    initialPageParam: '',
    getNextPageParam: ({ nextCursor }) => nextCursor,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

export const useWorkloadAutoscalerEventsQuery = (
  args: UseWorkloadEventsQueryArgs
): UseWorkloadEventsQuery => {
  const {
    isLoading,
    data,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  } = useInfiniteQuery(getWorkloadAutoscalerEventsQueryOptions(args));

  const events = useMemo(
    () => flatMap(data?.pages, (page) => page?.items),
    [data?.pages]
  );

  return {
    isLoading,
    events,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetching,
  };
};
